import React, {useState} from "react";
import {damsFetch} from "../app/damsFetch";
import {useDocumentState, useDocumentTranslation,} from "../documents/documentContext";
import {useAuthsState} from "../auths/authsContext";
import decamelizeKeysDeep from "decamelize-keys-deep";
import {InputRelatedOption} from "./InputRelatedOption";
import {useField, useFormikContext} from "formik";
import {DamsModal} from "../app/DamsModal";
import {ListRelations} from "./ListRelations";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import {fetchRelationDocs} from "./fetchRelationDocs";
import {clientLog} from "../clientLog";
import CircularProgress from "@mui/material/CircularProgress";
import {InputAdornment} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {SearchResultDetailsProvider} from "../search-result-details/SearchResultDetailsContext";

/**
 * @description
 * Component for selecting related objects.
 * @param {Object} props The props object.
 * @param {string} props.formikKey The formik key for the field.
 * @param {string} props.modelUniqueId The unique id of the current model.
 * @param {boolean} props.disabled Whether the field should be disabled. Defaults to false.
 * @param {string} props.helperText The helper text for the field. Defaults to null.
 * @param {Object} props.maxWidth The maximum width of the component. Defaults to '100%'.
 * @returns {React.ReactElement} A React component.
 */
export const InputRelatedObjects = ({
                                        formikKey,
                                        modelUniqueId,
                                        disabled = false,
                                        helperText = null,
                                        ...props
                                    }) => {
    const [field, meta] = useField(formikKey);
    const {setFieldValue} = useFormikContext();
    const [options, setOptions] = useState([]);
    const [chosenRelations, setChosenRelations] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const t = useDocumentTranslation();
    const {collectionIds} = useDocumentState();
    const {museumCollections} = useAuthsState();
    const [searching, setSearching] = useState(false);
    const museums = museumCollections
        .filter((museumCollection) =>
            collectionIds.includes(museumCollection.collectionId)
        )
        .map((museumCollection) => museumCollection.museumId);


    function debounce(fn, delay = 400) {
        let timer;
        return function (arg1, arg2) {
            clearTimeout(timer);
            timer = setTimeout(async () => {
                fn(arg1, arg2);
            }, delay);
        };
    }

    const searchCallback = models => {
        try {
            const results = models.filter(m => m.uniqueId !== modelUniqueId).map(m => {
                const {content, collectionId, uniqueId, title, description, documentType, createdAt, imageUrl} = m;
                const dmsId = content.mediae[0].reference.sourceId;
                const mimeType = content.mediae[0].reference['mime_type'];
                return {
                    collectionId: collectionId,
                    uniqueId: uniqueId,
                    title: title,
                    description: description,
                    documentType: documentType,
                    dmsId: dmsId,
                    mimeType: mimeType,
                    createdAt: createdAt,
                    content: content,
                    imageUrl: imageUrl || "",
                };
            });
            setOptions(prev => (results));
        } catch (e) {
            clientLog('error',
                `failed to parse search results: ${e.toString()}`,
                'inputrelatedobjects');
            setOptions([]);
            setSearching(false);
        }
    };

    const searchHandler = debounce(async (searchString) => {
        if (searchString.trim() === '' || searchString === '*' || searchString.length < 4) {
            setOptions([]);
            return;
        }
        try {
            setSearching(true);
            const searchParams = new URLSearchParams(
                decamelizeKeysDeep({
                    q: searchString,
                    sort: "title desc",
                    expand: true,
                    start: 0,
                    rows: 100,
                    documentType: "(StillImage OR Audio OR Video OR Misc)",
                    statsField: "created_at,updated_at,production_date",
                    museums: museums,
                    onlyMine: false,
                    folderContext: false
                })
            );

            const res = await damsFetch(`/search?${searchParams.toString()}`);
            const {models = []} = res;
            searchCallback(models);
            setSearching(false);
        } catch (e) {
            setSearching(false);
            clientLog('error',
                `failed to fetch list of objects: ${e.toString()}`,
                'inputrelatedobjects');
        }
    });

    const onDialogClose = (data) => {
        setOpenDialog(false);
        if (data) {
            saveHandler(data);
        }
    };

    const saveHandler = (data) => {
        const referenceMaps = [];
        for (let i = 0, max = data.length; i < max; i++) {
            const r = data[i];
            const ref = {
                referenceType: "relation",
                _action: "put",
                reference: {
                    locale: "no",
                    _action: "put",
                    title: r.title,
                    status: "published",
                    source: "dams",
                    sourceId: r.sourceId,
                    content: {
                        relationType: r.relationType,
                        relationDescription: r.relationDescription,
                    },
                },
            };
            referenceMaps.push(ref);
        }
        // Merge the last chosen value, with the already selected values,
        for (let i = 0, max = referenceMaps.length; i < max; i++) {
            const rm = referenceMaps[i];
            const ix = chosenRelations.findIndex(
                (cr) => cr.uniqueId === rm.reference.sourceId
            );
            if (ix > -1) {
                chosenRelations[ix] = rm;
            }
        }
        setFieldValue(field.name, chosenRelations).then();
    };

    const changeHandler = (_event, values, reason) => {
        if ("clear" === reason) {
            setChosenRelations([]);
        } else if ("removeOption" === reason) {
            deleteExisingRelations(values);

            // Remove from options
            if (values.length === 0) {
                // If removing the last related object, the "values" array length === 0.
                setFieldValue(field.name, []).then();
            } else {
                const fieldValues = field.value;
                for (let i = 0, max = values.length; i < max; i++) {
                    const toBeDeletedId = values[i].reference.id;
                    const ix = fieldValues.findIndex(
                        (fv) => fv.reference.id !== toBeDeletedId
                    );
                    fieldValues.splice(ix, 1);
                }
                setFieldValue(field.name, fieldValues).then();
            }
        } else if ("createOption" !== reason) {
            if (values.length > 0) {
                setChosenRelations(values);
                setOpenDialog(true);
            }
        }
    };

    /**
     * Deletes existing/saved/indexed relations.
     * @param values
     */
    const deleteExisingRelations = (values) => {
        if (values && values.length > 0) {
            const docUniqueIds = values
                .filter((_) => true)
                .map((f) => f.reference.id);

            (async () => {
                const res = await fetchRelationDocs(docUniqueIds);
                if (res && res.length === 0) {
                    return;
                }

                function getDoc(uniqueId) {
                    return res.find((f) => {
                        return f.uniqueId === uniqueId;
                    });
                }

                const mappedValues = values.map((v) => {
                    return {
                        title: v.reference.title,
                        sourceId: v.reference.sourceId,
                        relationType: getDoc(v.reference.uniqueId).relationType,
                        relationDescription: getDoc(v.reference.uniqueId)
                            .relationDescription,
                    };
                });
                saveHandler(mappedValues);
            })();
        } else {
            saveHandler([]);
        }
    };

    const getMaxWidthSetting = () => {
        let maxWidth = null;
        if (Object.keys(props).includes('maxWidth')) {
            maxWidth = props.maxWidth;
            delete props.maxWidth;
        }
        return maxWidth;
    };

    const renderInput = (params) => {
        // Destructure deprecated props so they are never spread onto TextField
        // as top-level props — MUI v9 forwards them to DOM elements otherwise.
        const {InputProps, inputProps: _inputProps, ...textFieldParams} = params;
        return <TextField
            {...textFieldParams}
            label={t("txtNoRelatedObjects", "Relaterte objekter")}
            helperText={helperText}
            slotProps={{
                ...params.slotProps,
                input: {
                    ...(params.slotProps?.input ?? InputProps ?? {}),
                    endAdornment: (
                        <>
                            <InputAdornment position="end">
                                <SearchIcon/>
                            </InputAdornment>
                            {(params.slotProps?.input ?? InputProps)?.endAdornment}
                        </>
                    ),
                }
            }}
        />;
    };

    const renderOption = (props, option) => {
        return (
            <Box {...props} key={crypto.randomUUID()}>
                <InputRelatedOption {...option} />
            </Box>
        );
    };

    const inputChangeHandler = (_event, newInputValue) => {
        searchHandler(newInputValue, 500);
    };

    if (undefined === meta.initialValue) {
        return null;
    } else {
        let maxWidth = getMaxWidthSetting();
        return (
            <>
                <Autocomplete
                    sx={maxWidth !== null ? {maxWidth: props.maxWidth} : {}}
                    id="relatedObjects"
                    disabled={disabled}
                    freeSolo
                    multiple
                    autoComplete={true}
                    filterSelectedOptions={true}
                    options={options}
                    value={field.value}
                    loading={Boolean(searching)}
                    loadingText={
                        <Box>
                            <CircularProgress size={16}
                                              color="secondary"
                                              sx={{
                                                  marginRight: '16px'
                                              }}/>
                            {t("searchingPlaceholder", "Søker...")}
                        </Box>
                    }
                    noOptionsText={t("txtNoDocuments", "Ingen dokumenter")}
                    getOptionLabel={(option) => {
                        return option.title || option.reference.title || "";
                    }}
                    filterOptions={() => {
                        return options;
                    }}
                    onChange={changeHandler}
                    onInputChange={inputChangeHandler}
                    renderInput={renderInput}
                    renderOption={renderOption}
                    {...props}
                />
                <DamsModal open={openDialog}>
                    <SearchResultDetailsProvider>
                        <ListRelations relations={chosenRelations} callback={onDialogClose}/>
                    </SearchResultDetailsProvider>
                </DamsModal>
            </>
        );
    }
};
